import React from "react";
import { StoreContextProvider } from "src/context/siteContext";
import { eventData } from "src/utils/dataLayer";
import { ThemeProvider } from "styled-components";
import { GlobalStyle, theme } from "src/theme/globalStyle";
import "./src/styles/fonts.css";

const geoRedirect = async () => {
  const results = await fetch("/api/geo");
  const geo = await results.json();
  const isUK = process.env.GATSBY_SANITY_DATASET === "production";

  if (typeof window !== "undefined") {
    if ((geo.country == "US" && isUK) || (geo.country == "GB" && !isUK)) {
      window.openRedirectModal = true;
    } else {
      window.openRedirectModal = false;
    }
  }
};

export async function awinIntegration() {
  if (typeof window === 'undefined') return; // only run in browser

  const advertiserId = process.env.GATSBY_SANITY_DATASET === "production" ? 120706 : 123974;
  const cookieDomain = process.env.GATSBY_SANITY_DATASET === "production" ? ".groovepillows.co.uk" : ".groovepillow.com";

  const readCookiesAsString = (awRegEx) => {
    const aCookies = document.cookie.split(';');
    const cookies = [];
    for (let i = 0; i < aCookies.length; i++) {
      const aParts = aCookies[i].split('=');
      if (awRegEx.test(aParts[0])) cookies.push(aParts[1]);
    }
    return cookies.join(',');
  };

  const getAwc = () => {
    const rx = new RegExp('[?&]awc=([^&]+).*$');
    const returnVal = document.location.href.match(rx);

    if (returnVal?.[1]?.match(/[0-9]+_[0-9]+_[a-zA-Z0-9]+/)) {
      return returnVal[1];
    }

    const awcFromCookie = readCookiesAsString(/_aw_m_\d+/);
    const awcFromSnCookie = readCookiesAsString(/_aw_sn_\d+/);

    if (awcFromCookie && awcFromSnCookie) return `${awcFromCookie},${awcFromSnCookie}`;
    return awcFromCookie || awcFromSnCookie || '';
  };

  const getSource = () => {
    const rx = new RegExp('[?&]source=([^&]+).*$');
    const returnVal = document.location.href.match(rx);

    if (returnVal) {
      return returnVal[1].replace(/[^a-zA-Z0-9-_]/g, '');
    }

    const sourceFromCookie = readCookiesAsString(/_aw_channel$/);
    return sourceFromCookie || '';
  };

  const setPxCookie = (awc) => {
    const cookieName = `_awc_${advertiserId}`;
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `${cookieName}=${awc};expires=${expires.toGMTString()};path=/;domain=${cookieDomain}`;
  };

  const setSourceCookie = (source, expiresOverrideDate) => {
    const cookieName = '_aw_channel';
    const expires = new Date(
      expiresOverrideDate?.getTime() ?? Date.now() + 365 * 24 * 60 * 60 * 1000
    );
    const cookieValue =
      source.includes('|') ? source : `${source}|${Math.round(expires.getTime() / 1000)}`;

    document.cookie = `${cookieName}=${cookieValue};expires=${expires.toGMTString()};path=/;domain=${cookieDomain}`;
  };

  const addMasterTag = (advertiserId) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = `https://www.dwin1.com/${advertiserId}.js`;
    document.body.appendChild(script);
  };

  const setCartNoteAttributes = async (pAwc, pSource) => {
    const noteAttributes = { __awin_channel: pSource };
    if (pAwc) noteAttributes.__awc = pAwc;

    try {
      await fetch('https://groovepillows.myshopify.com/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          note: '',
          attributes: noteAttributes,
        }),
        headers: { 'Content-type': 'application/json' },
      });
    } catch (err) {
      console.warn('Awin cart update failed:', err);
    }
  };

  const run = async () => {
    const awc = getAwc(advertiserId);
    const fullSourceValue = getSource();

    if (!advertiserId) return;

    if (awc) {
      const source = fullSourceValue.split('|')[0];
      await setCartNoteAttributes(awc, source);
      setPxCookie(awc);
    }

    if (fullSourceValue) {
      if (fullSourceValue.includes('|')) {
        const [_, timestamp] = fullSourceValue.split('|');
        const sourceDate = new Date(parseInt(timestamp) * 1000);
        setSourceCookie(fullSourceValue, sourceDate);
      } else {
        setSourceCookie(fullSourceValue);
      }
    }

    addMasterTag(advertiserId);
  };

  await run();
}

export const onClientEntry = async () => {
  try {
    await geoRedirect();
    await awinIntegration();
  } catch (e) {
    console.log(e);
  }
};

export const onRouteUpdate = ({ location, previousLocation }) => {
  // app.analytics.pageview(location);
  window.dataLayer?.push({
    ...eventData("dl_user_data")
  });

  if (location && location.hash) {
    const item = document.getElementById(location.hash);

    if (item) {
      item.scrollIntoView({ behavior: 'smooth' });
    }
  }

  return true;
};

export const shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition
}) => {
  return true;
};

// Optional Config Sentry
// Sentry.init({ dsn: process.env.GATSBY_SENTRY_DSN });

export const wrapPageElement = ({ element }) => (
  <StoreContextProvider>
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {element}
    </ThemeProvider>
  </StoreContextProvider>
);
